
export interface EbookBookmark {
  id: string;
  page: number;
  createdAt: string;
  label?: string;
}

export interface EbookHighlight {
  id: string;
  page: number;
  color: 'yellow' | 'green' | 'blue' | 'pink';
  text: string;
  createdAt: string;
}

export interface EbookNote {
  id: string;
  page: number;
  text: string;
  createdAt: string;
  updatedAt: string;
}

export interface EbookReadingProgress {
  ebookId: string;
  ebookTitle: string;
  currentPage: number;
  totalPages: number;
  lastReadAt: string;
  completedAt?: string;
  timeSpentMinutes: number;
  bookmarks?: EbookBookmark[];
  highlights?: EbookHighlight[];
  notes?: EbookNote[];
  fontSize?: 'small' | 'medium' | 'large' | 'xlarge';
  theme?: 'light' | 'sepia' | 'dark';
}

// Blog Types
export interface BlogCategory {
  id: string;
  name: string;
  slug: string;
  description?: string;
  color?: string;
  postCount?: number;
}

export interface BlogComment {
  id: string;
  postId: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  content: string;
  createdAt: string;
  updatedAt?: string;
  likes: string[]; // Array of user IDs who liked the comment
  parentId?: string | null; // null for top-level, comment_id for replies
}

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string; // Short description for listing
  content: string; // Rich HTML content
  featuredImage?: string; // Base64 or URL
  author: {
    id: string;
    name: string;
    avatar?: string;
  };
  category: string; // Category ID
  tags: string[];
  status: 'draft' | 'published';
  metaDescription?: string; // SEO
  views: number;
  likes: string[]; // Array of user IDs who liked
  bookmarkedBy: string[]; // Array of user IDs who bookmarked
  comments: BlogComment[];
  readingTimeMinutes: number;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
}

export interface UserProfile {
  id: string;
  username: string;
  displayName?: string;
  realName?: string;
  guruPradattNaam?: string;
  sect?: string; // Sampradaye - optional spiritual tradition/lineage
  city?: string; // City/location of the sadhak
  bio?: string;
  avatar?: string; // Base64 avatar string
  email?: string;
  dob?: string;
  mobile?: string;
  total_count: number;
  mantra_counts: Record<string, number>; // Aggregate count
  likhit_counts?: Record<string, number>; // Specific to written
  smaran_counts?: Record<string, number>; // Specific to tap/mental
  vani_counts?: Record<string, number>; // Specific to voice (Renamed from Kalam)
  nidhi_counts?: Record<string, number>; // Specific to OCR/Notebook scans
  streak_days: number;
  joined_date: string;
  saved_audios?: string[]; // Array of audio IDs for personal playlist
  saved_blogs?: string[]; // Array of blog post IDs bookmarked by user
  legacy_config?: LegacyConfig; // Spiritual Will
  prasadam_gallery?: any[]; // DEPRECATED - kept for backward compatibility
  language?: 'en' | 'hi'; // New: Multilingual support
  parikrama_steps?: number;
  nitya_seva_state?: NityaSevaState;
  ebook_progress?: EbookReadingProgress[]; // Ebook reading progress tracking
  isGuest?: boolean; // Guest user flag - can explore but can't save data
  isOfflineSession?: boolean; // Track if session started while offline
}

export interface NityaSevaOffering {
  id: string;
  type: string;
  timestamp: number;
  x: number;
  y: number;
}

export interface NityaSevaState {
  last_cleaned: number;
  diya_lit_until: number;
  deity: string;
  offerings: NityaSevaOffering[];
}

export interface AppNotification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'system' | 'milestone';
  timestamp: string;
  read: boolean;
  link?: string; // Optional internal link or action
}

export interface LegacyHeir {
  id: string;
  name: string;
  type: 'Individual' | 'Temple' | 'Global Cause';
  share: number; // percentage 0-100
}

export interface LegacyConfig {
  heirs: LegacyHeir[];
  inactivityTriggerDays: number;
  isFinalized: boolean;
  finalizedAt?: string;
}

export interface JaapLog {
  id: string;
  user_id: string;
  count: number;
  timestamp: string;
}

export interface LeaderboardEntry {
  rank: number;
  username: string;
  count: number;
  badge?: 'Gold' | 'Silver' | 'Bronze';
}

export interface Banner {
  id: string;
  imageUrl: string;
  title: string;
  link?: string; // Target URL for the banner
  active: boolean;
}

export interface BrandingConfig {
  appName: string;
  subTitle?: string;
  logoUrl?: string; // Base64
  logoHeight?: number; // Logo height in pixels
}

export interface ReminderConfig {
  enabled: boolean;
  intervalMinutes: number; // e.g. 15, 30, 60
  mantraText: string; // The text to speak
  mantraId?: string; // Mantra ID for audio lookup
  mantraSoundName?: string; // res/raw filename e.g. 'mantra_ram'
  nextFireTime?: number; // Timestamp of next alarm fire
  volume: number; // 0 to 1
  useCustomAudio?: boolean; // Toggle between TTS and Custom Audio
  customAudioData?: string; // Base64 string of the audio file
}

export interface SocialLinks {
  facebook: string;
  instagram: string;
  youtube: string;
  twitter: string;
  whatsapp: string;
  telegram: string;
}

export interface ContactInfo {
  phone: string;
  email: string;
  address: string;
  whatsapp: string;
  supportHours: string;
  website?: string;
  mapEmbedUrl?: string;
}

export interface Mantra {
  id: string;
  label: string;
  hindiLabel: string;
  placeholder: string;
  heading: string;
  subtext: string;
  patterns: string[];
  keypadKeys: string[];
}

export interface AkhandRoom {
  id: string;
  name: string;
  hindiName: string;
  mantraId: string;
  participantCount: number;
  collectiveTotal: number;
  description: string;
  creatorId?: string; // Added for moderation
}

// Chapter/Index for Ebooks
export interface EbookChapter {
  id: string;
  name: string;
  pageNumber: number;
}

export interface Ebook {
  id: string;
  title: string;
  author: string;
  category: string;
  coverImage: string;
  pdfData?: string; // Base64 for PDF (legacy)
  pdfUrl?: string; // URL for PDF file (legacy)
  storagePath?: string; // Object Storage path (new)
  epubUrl?: string; // URL for EPUB file
  fileType: 'pdf' | 'epub'; // File type indicator
  active: boolean;
  isPublished?: boolean; // Backend uses this field
  uploadedAt: string;
  chapters?: EbookChapter[]; // Table of Contents / Index
  allowDownload?: boolean; // Admin setting: Allow users to download
  allowScreenshot?: boolean; // Admin setting: Allow users to take screenshots
}

export const AUDIO_CATEGORIES = [
  "Pujya Bhaiji Hanuman Prasad Poddar Ji Satsang",
  "Pujya Seth Shri Jayadayal Goendka Ji Satsang",
  "Pujya Swami Shri Ramsukhdas Ji Satsang"
];

export const EBOOK_CATEGORIES = [
  "Spiritual Texts",
  "Bhajans & Kirtans",
  "Pravachans"
];

export interface SatsangAudio {
  id: string;
  title: string;
  hindiTitle?: string;
  speaker: string;
  description?: string;
  category: string;
  audioData?: string; // Base64 (legacy/IndexedDB)
  audioUrl?: string; // URL from backend
  thumbnailUrl?: string;
  duration?: string;
  views?: number;
  likes?: string[];
  active?: boolean;
  isActive?: boolean;
  uploadedAt?: string;
  createdAt?: string;
}

export interface SatsangVideo {
  id: string;
  title: string;
  youtubeUrl: string;
  thumbnail: string;
  category: string;
  active: boolean;
  uploadedAt: string;
}

export interface NidhiScan {
  id: string;
  userId: string;
  username: string;
  imageData: string; // Base64 image of the scanned page
  mantraId: string;
  mantraName: string;
  count: number;
  confidence: number;
  reasoning: string;
  scannedAt: string;
}

export enum Tab {
  HOME = 'HOME',
  JAAP = 'JAAP',
  DASHBOARD = 'DASHBOARD',
  AKHAND_JAAP = 'AKHAND_JAAP',
  SADHANA_INSIGHTS = 'SADHANA_INSIGHTS',
  NIDHI_OCR = 'NIDHI_OCR',
  HALL_OF_FAME = 'HALL_OF_FAME',
  CERTIFICATE = 'CERTIFICATE',
  ADMIN = 'ADMIN',
  EBOOKS = 'EBOOKS',
  SATSANG_AUDIOS = 'SATSANG_AUDIOS',
  AI_SANT_VANI = 'AI_SANT_VANI',
  PROFILE = 'PROFILE',
  ABOUT_US = 'ABOUT_US',
  CONTACT_US = 'CONTACT_US',
  SHLOKA_OF_DAY = 'SHLOKA_OF_DAY',
  BLOGS = 'BLOGS',
  SADHAK_TEST = 'SADHAK_TEST',
  ADMIN_SADHAK_TEST = 'ADMIN_SADHAK_TEST',
  NOTES = 'NOTES',
  ALBUM_GALLERY = 'ALBUM_GALLERY',
  ADMIN_ALBUM = 'ADMIN_ALBUM',
  FAVORITES = 'FAVORITES',
  PUSTIKA_REGISTER = 'PUSTIKA_REGISTER',
  ADMIN_PUSTIKA = 'ADMIN_PUSTIKA',
  VIDEOS = 'VIDEOS',
  ADMIN_VIDEOS = 'ADMIN_VIDEOS',
  POEMS = 'POEMS',
  POEM_DETAIL = 'POEM_DETAIL',
  LEELA_CHINTAN = 'LEELA_CHINTAN',
  TEXT_READER = 'TEXT_READER',
  SARVATRA_DARSHAN = 'SARVATRA_DARSHAN'
}

// Album/Gallery Types
export interface AlbumCategory {
  id: string;
  name: string;
  description?: string;
  coverImage?: string;
  order: number;
  createdAt: string;
}

export interface AlbumImage {
  id: string;
  categoryId: string;
  title: string;
  imageData: string; // base64 or URL
  isUrl: boolean; // true if external URL, false if base64
  createdAt: string;
}

// Default album categories
export const DEFAULT_ALBUM_CATEGORIES: AlbumCategory[] = [
  { id: 'temples', name: 'Temple Photos', description: 'Sacred temples and shrines', order: 0, createdAt: new Date().toISOString() },
  { id: 'festivals', name: 'Festival Celebrations', description: 'Divine festival moments', order: 1, createdAt: new Date().toISOString() },
  { id: 'saints', name: 'Saints & Gurus', description: 'Holy saints and spiritual masters', order: 2, createdAt: new Date().toISOString() },
  { id: 'art', name: 'Divine Art', description: 'Spiritual artwork and paintings', order: 3, createdAt: new Date().toISOString() },
];

// Shloka/Quote of the Day Types
export type ShlokaType = 'shloka' | 'quote';

export interface DailyShloka {
  id: string;
  type: ShlokaType;
  sanskritText: string;           // Original Sanskrit text
  hindiTransliteration: string;   // Hindi transliteration (Roman to Devanagari)
  hindiMeaning: string;           // Hindi translation/meaning
  englishMeaning?: string;        // Optional English translation
  source: string;                 // e.g., "Bhagavad Gita 2.47" or author name for quotes
  chapter?: number;               // Chapter number (for Gita shlokas)
  verse?: number;                 // Verse number (for Gita shlokas)
  author?: string;                // Author name (for quotes)
  tags?: string[];                // Tags like "karma", "dharma", "devotion"
  active: boolean;
  createdAt: string;
}


// Sidebar Menu Item for reordering
export interface SidebarMenuItem {
  id: string;
  label: string;
  tab?: Tab;
  icon: string;
  isSpecial?: boolean;  // For highlighted items like AI Vani
  hasDropdown?: boolean; // For ebooks/audios with categories
  externalLink?: string; // For external URLs like golokstore.com
  order: number;
  visible: boolean;
}

// Default sidebar menu items
export const DEFAULT_SIDEBAR_MENU: SidebarMenuItem[] = [
  { id: 'ai_vani', label: 'AI Sant Vani', tab: Tab.AI_SANT_VANI, icon: 'Sparkles', isSpecial: true, order: 0, visible: true },
  { id: 'sadhak_test', label: 'आत्म-परीक्षण', tab: Tab.SADHAK_TEST, icon: 'Compass', isSpecial: true, order: 1, visible: true },
  { id: 'favorites', label: 'My Favorites', tab: Tab.FAVORITES, icon: 'Heart', isSpecial: true, order: 2, visible: true },
  { id: 'notes', label: 'My Notes', tab: Tab.NOTES, icon: 'StickyNote', order: 3, visible: true },
  { id: 'videos', label: 'Divine Videos', tab: Tab.VIDEOS, icon: 'PlayCircle', isSpecial: false, order: 4, visible: true },
  { id: 'poems', label: 'दिव्य काव्य', tab: Tab.POEMS, icon: 'Quote', isSpecial: false, order: 5, visible: true },
  { id: 'shloka_day', label: 'Shloka of the Day', tab: Tab.SHLOKA_OF_DAY, icon: 'Quote', order: 6, visible: true },
  { id: 'ekadashi', label: 'Ekadashi Calendar', icon: 'Moon', order: 7, visible: true },
  { id: 'blogs', label: 'Divine Wisdom Blog', tab: Tab.BLOGS, icon: 'FileText', order: 8, visible: true },
  { id: 'buy_books', label: 'Buy Books', icon: 'ShoppingBag', externalLink: 'https://golokstore.com', order: 9, visible: true },
  { id: 'scanner', label: 'Naam-Lekhan Scanner', tab: Tab.NIDHI_OCR, icon: 'Scan', order: 11, visible: true },
  { id: 'insights', label: 'Sadhana Insights', tab: Tab.SADHANA_INSIGHTS, icon: 'Compass', order: 12, visible: true },
  { id: 'sangha', label: 'Sangha', tab: Tab.AKHAND_JAAP, icon: 'Users', order: 13, visible: true },
  { id: 'ebooks', label: 'Divine Ebooks', icon: 'BookOpen', hasDropdown: true, order: 14, visible: true },
  { id: 'audios', label: 'Satsang Audios', icon: 'Music', hasDropdown: true, order: 15, visible: true },
  { id: 'album', label: 'Divine Gallery', icon: 'ImageIcon', hasDropdown: true, order: 16, visible: true },
  { id: 'about', label: 'About Us', tab: Tab.ABOUT_US, icon: 'Info', order: 17, visible: true },
  { id: 'leela_chintan', label: 'Leela Chintan', tab: Tab.LEELA_CHINTAN, icon: 'Palette', isSpecial: false, order: 3, visible: true },
  { id: 'sarvatra_darshan', label: 'सर्वत्र भगवान् के दर्शन', tab: Tab.SARVATRA_DARSHAN, icon: 'Eye', isSpecial: false, order: 3.5, visible: true },
  { id: 'text_reader', label: 'पाठ्य पुस्तकें', tab: Tab.TEXT_READER, icon: 'Book', isSpecial: false, order: 4, visible: false },
  { id: 'contact', label: 'Contact Us', tab: Tab.CONTACT_US, icon: 'MessageSquare', order: 18, visible: true },
];

// Ekadashi Types (Vaishnav only)
export interface EkadashiDate {
  date: string;           // YYYY-MM-DD format (Vaishnav fast date)
  name: string;           // Ekadashi name
  paksha: 'Shukla' | 'Krishna';  // Bright/Dark fortnight
  month: string;          // Hindu month name
  description?: string;   // Brief description
  smpikaStart?: string;   // Ekadashi tithi start time
  smpikaEnd?: string;     // Ekadashi tithi end time
  // Vaishnav Ekadashi (वैष्णव) - Dashami must end before Arunodaya
  vaishnavDate?: string;   // Alias for date
  vaishnavParanStart?: string; // Vaishnav Paran start  
  vaishnavParanEnd?: string;   // Vaishnav Paran end
  vaishnavParanDate?: string;  // Paran date (if different from next day)
  vaishnavParanNote?: string;  // Special note
}

// Ekadashi dates for 2025-2027 (based on Drik Panchang for New Delhi, India)
// Times are in IST. Vaishnav Ekadashi only - Dashami must end before Arunodaya
export const EKADASHI_DATES: EkadashiDate[] = [
  // 2025
  { 
    date: '2025-01-10', name: 'Pausha Putrada Ekadashi', paksha: 'Shukla', month: 'Pausha', 
    description: 'For blessing of sons',
    smpikaStart: 'Jan 09, 08:47 PM', smpikaEnd: 'Jan 10, 08:22 PM',
    vaishnavDate: '2025-01-10', vaishnavParanStart: '07:15 AM', vaishnavParanEnd: '09:27 AM', vaishnavParanNote: 'Jan 11',
  },
  { 
    date: '2025-01-25', name: 'Shattila Ekadashi', paksha: 'Krishna', month: 'Magha', 
    description: 'Observance with sesame seeds',
    smpikaStart: 'Jan 24, 08:57 AM', smpikaEnd: 'Jan 25, 07:14 AM',
    vaishnavDate: '2025-01-25', vaishnavParanStart: '07:14 AM', vaishnavParanEnd: '09:26 AM', vaishnavParanNote: 'Jan 26',
  },
  { 
    date: '2025-02-09', name: 'Jaya Ekadashi', paksha: 'Shukla', month: 'Magha', 
    description: 'Victory over sins',
    smpikaStart: 'Feb 08, 07:28 AM', smpikaEnd: 'Feb 09, 08:04 AM',
    vaishnavDate: '2025-02-09', vaishnavParanStart: '07:05 AM', vaishnavParanEnd: '09:17 AM', vaishnavParanNote: 'Feb 10',
  },
  { 
    date: '2025-02-23', name: 'Vijaya Ekadashi', paksha: 'Krishna', month: 'Phalguna', 
    description: 'Success in endeavors',
    smpikaStart: 'Feb 22, 07:12 PM', smpikaEnd: 'Feb 23, 05:44 PM',
    vaishnavDate: '2025-02-23', vaishnavParanStart: '06:50 AM', vaishnavParanEnd: '09:02 AM', vaishnavParanNote: 'Feb 24',
  },
  { 
    date: '2025-03-10', name: 'Amalaki Ekadashi', paksha: 'Shukla', month: 'Phalguna', 
    description: 'Worship of Amalaki tree',
    smpikaStart: 'Mar 09, 05:27 PM', smpikaEnd: 'Mar 10, 06:48 PM',
    vaishnavDate: '2025-03-10', vaishnavParanStart: '06:32 AM', vaishnavParanEnd: '08:42 AM', vaishnavParanNote: 'Mar 11',
  },
  { 
    date: '2025-03-25', name: 'Papamochani Ekadashi', paksha: 'Krishna', month: 'Chaitra', 
    description: 'Destroyer of sins',
    smpikaStart: 'Mar 24, 04:30 AM', smpikaEnd: 'Mar 25, 03:18 AM',
    vaishnavDate: '2025-03-24', vaishnavParanStart: '06:12 AM', vaishnavParanEnd: '08:22 AM', vaishnavParanNote: 'Mar 25',
  },
  { 
    date: '2025-04-09', name: 'Kamada Ekadashi', paksha: 'Shukla', month: 'Chaitra', 
    description: 'Fulfiller of desires',
    smpikaStart: 'Apr 08, 02:36 AM', smpikaEnd: 'Apr 09, 04:27 AM',
    vaishnavDate: '2025-04-09', vaishnavParanStart: '05:52 AM', vaishnavParanEnd: '08:01 AM', vaishnavParanNote: 'Apr 10',
  },
  { 
    date: '2025-04-23', name: 'Varuthini Ekadashi', paksha: 'Krishna', month: 'Vaishakha', 
    description: 'Protective fasting',
    smpikaStart: 'Apr 22, 12:43 PM', smpikaEnd: 'Apr 23, 11:44 AM',
    vaishnavDate: '2025-04-23', vaishnavParanStart: '05:35 AM', vaishnavParanEnd: '07:44 AM', vaishnavParanNote: 'Apr 24',
  },
  { 
    date: '2025-05-08', name: 'Mohini Ekadashi', paksha: 'Shukla', month: 'Vaishakha', 
    description: 'Enchanting form of Vishnu',
    smpikaStart: 'May 07, 11:43 AM', smpikaEnd: 'May 08, 01:46 PM',
    vaishnavDate: '2025-05-08', vaishnavParanStart: '05:22 AM', vaishnavParanEnd: '07:30 AM', vaishnavParanNote: 'May 09',
  },
  { 
    date: '2025-05-23', name: 'Apara Ekadashi', paksha: 'Krishna', month: 'Jyeshtha', 
    description: 'Unlimited merit',
    smpikaStart: 'May 22, 07:30 PM', smpikaEnd: 'May 23, 06:48 PM',
    vaishnavDate: '2025-05-24', vaishnavParanStart: '05:15 AM', vaishnavParanEnd: '07:23 AM', vaishnavParanNote: 'May 25',
  },
  { 
    date: '2025-06-06', name: 'Nirjala Ekadashi', paksha: 'Shukla', month: 'Jyeshtha', 
    description: 'Waterless fasting - most meritorious',
    smpikaStart: 'Jun 05, 09:50 PM', smpikaEnd: 'Jun 06, 11:37 PM',
    vaishnavDate: '2025-06-06', vaishnavParanStart: '05:13 AM', vaishnavParanEnd: '07:21 AM', vaishnavParanNote: 'Jun 07',
  },
  { 
    date: '2025-06-21', name: 'Yogini Ekadashi', paksha: 'Krishna', month: 'Ashadha', 
    description: 'Union with divine',
    smpikaStart: 'Jun 21, 12:07 AM', smpikaEnd: 'Jun 21, 11:45 PM',
    vaishnavDate: '2025-06-21', vaishnavParanStart: '05:16 AM', vaishnavParanEnd: '07:24 AM', vaishnavParanNote: 'Jun 22',
  },
  { 
    date: '2025-07-06', name: 'Devshayani Ekadashi', paksha: 'Shukla', month: 'Ashadha', 
    description: 'Vishnu goes to sleep - Chaturmas begins',
    smpikaStart: 'Jul 05, 09:44 AM', smpikaEnd: 'Jul 06, 10:50 AM',
    vaishnavDate: '2025-07-06', vaishnavParanStart: '05:23 AM', vaishnavParanEnd: '07:31 AM', vaishnavParanNote: 'Jul 07',
  },
  { 
    date: '2025-07-20', name: 'Kamika Ekadashi', paksha: 'Krishna', month: 'Shravana', 
    description: 'During Chaturmas',
    smpikaStart: 'Jul 20, 04:44 AM', smpikaEnd: 'Jul 21, 04:51 AM',
    vaishnavDate: '2025-07-21', vaishnavParanStart: '05:32 AM', vaishnavParanEnd: '07:40 AM', vaishnavParanNote: 'Jul 22',
  },
  { 
    date: '2025-08-04', name: 'Shravana Putrada Ekadashi', paksha: 'Shukla', month: 'Shravana', 
    description: 'For blessing of sons',
    smpikaStart: 'Aug 03, 10:37 PM', smpikaEnd: 'Aug 04, 10:43 PM',
    vaishnavDate: '2025-08-04', vaishnavParanStart: '05:42 AM', vaishnavParanEnd: '07:49 AM', vaishnavParanNote: 'Aug 05',
  },
  { 
    date: '2025-08-19', name: 'Aja Ekadashi', paksha: 'Krishna', month: 'Bhadrapada', 
    description: 'Removes great sins',
    smpikaStart: 'Aug 18, 09:29 AM', smpikaEnd: 'Aug 19, 10:10 AM',
    vaishnavDate: '2025-08-19', vaishnavParanStart: '05:53 AM', vaishnavParanEnd: '07:59 AM', vaishnavParanNote: 'Aug 20',
  },
  { 
    date: '2025-09-03', name: 'Parsva Ekadashi', paksha: 'Shukla', month: 'Bhadrapada', 
    description: 'Vishnu turns on his side',
    smpikaStart: 'Sep 02, 11:54 AM', smpikaEnd: 'Sep 03, 10:55 AM',
    vaishnavDate: '2025-09-03', vaishnavParanStart: '06:03 AM', vaishnavParanEnd: '08:09 AM', vaishnavParanNote: 'Sep 04',
  },
  { 
    date: '2025-09-17', name: 'Indira Ekadashi', paksha: 'Krishna', month: 'Ashwin', 
    description: 'Liberation of ancestors',
    smpikaStart: 'Sep 17, 02:12 PM', smpikaEnd: 'Sep 18, 03:24 PM',
    vaishnavDate: '2025-09-18', vaishnavParanStart: '06:14 AM', vaishnavParanEnd: '08:19 AM', vaishnavParanNote: 'Sep 19',
  },
  { 
    date: '2025-10-01', name: 'Papankusha Ekadashi', paksha: 'Shukla', month: 'Ashwin', 
    description: 'Hook to destroy sins',
    smpikaStart: 'Oct 01, 01:13 AM', smpikaEnd: 'Oct 01, 11:19 PM',
    vaishnavDate: '2025-10-01', vaishnavParanStart: '06:24 AM', vaishnavParanEnd: '08:28 AM', vaishnavParanNote: 'Oct 02',
  },
  { 
    date: '2025-10-17', name: 'Rama Ekadashi', paksha: 'Krishna', month: 'Kartik', 
    description: 'Dedicated to Lord Rama',
    smpikaStart: 'Oct 16, 07:03 PM', smpikaEnd: 'Oct 17, 08:43 PM',
    vaishnavDate: '2025-10-17', vaishnavParanStart: '06:36 AM', vaishnavParanEnd: '08:39 AM', vaishnavParanNote: 'Oct 18',
  },
  { 
    date: '2025-10-31', name: 'Devutthana Ekadashi', paksha: 'Shukla', month: 'Kartik', 
    description: 'Vishnu awakens - Tulsi Vivah',
    smpikaStart: 'Oct 30, 12:42 PM', smpikaEnd: 'Oct 31, 09:58 AM',
    vaishnavDate: '2025-10-31', vaishnavParanStart: '06:48 AM', vaishnavParanEnd: '08:50 AM', vaishnavParanNote: 'Nov 01',
  },
  { 
    date: '2025-11-15', name: 'Utpanna Ekadashi', paksha: 'Krishna', month: 'Margashirsha', 
    description: 'Birth of Ekadashi Devi',
    smpikaStart: 'Nov 14, 10:35 PM', smpikaEnd: 'Nov 16, 12:39 AM',
    vaishnavDate: '2025-11-15', vaishnavParanStart: '07:00 AM', vaishnavParanEnd: '09:01 AM', vaishnavParanNote: 'Nov 16',
  },
  { 
    date: '2025-11-29', name: 'Mokshada Ekadashi', paksha: 'Shukla', month: 'Margashirsha', 
    description: 'Giver of liberation - Gita Jayanti',
    smpikaStart: 'Nov 28, 09:32 PM', smpikaEnd: 'Nov 29, 07:19 PM',
    vaishnavDate: '2025-11-29', vaishnavParanStart: '07:09 AM', vaishnavParanEnd: '09:10 AM', vaishnavParanNote: 'Nov 30',
  },
  { 
    date: '2025-12-14', name: 'Saphala Ekadashi', paksha: 'Krishna', month: 'Pausha', 
    description: 'Fruitful fasting',
    smpikaStart: 'Dec 14, 02:37 AM', smpikaEnd: 'Dec 15, 05:04 AM',
    vaishnavDate: '2025-12-15', vaishnavParanStart: '07:18 AM', vaishnavParanEnd: '09:18 AM', vaishnavParanNote: 'Dec 16',
  },
  { 
    date: '2025-12-29', name: 'Pausha Putrada Ekadashi', paksha: 'Shukla', month: 'Pausha', 
    description: 'For blessing of sons',
    smpikaStart: 'Dec 28, 05:04 AM', smpikaEnd: 'Dec 29, 03:43 AM',
    vaishnavDate: '2025-12-28', vaishnavParanStart: '07:21 AM', vaishnavParanEnd: '09:20 AM', vaishnavParanNote: 'Dec 29',
  },
  // 2026 - Updated from Drik Panchang (New Delhi, IST)
  { 
    date: '2026-01-14', name: 'Shattila Ekadashi', paksha: 'Krishna', month: 'Magha', 
    description: 'Observance with sesame seeds',
    smpikaStart: 'Jan 13, 03:17 PM', smpikaEnd: 'Jan 14, 05:52 PM',
    vaishnavDate: '2026-01-14', vaishnavParanStart: '07:22 AM', vaishnavParanEnd: '09:21 AM', vaishnavParanNote: 'Jan 15',
  },
  { 
    date: '2026-01-28', name: 'Jaya Ekadashi', paksha: 'Shukla', month: 'Magha', 
    description: 'Victory over sins',
    smpikaStart: 'Jan 28, 04:35 PM', smpikaEnd: 'Jan 29, 01:55 PM',
    vaishnavDate: '2026-01-29', vaishnavParanStart: '07:16 AM', vaishnavParanEnd: '09:15 AM', vaishnavParanNote: 'Jan 30',
  },
  { 
    date: '2026-02-13', name: 'Vijaya Ekadashi', paksha: 'Krishna', month: 'Phalguna', 
    description: 'Success in endeavors',
    smpikaStart: 'Feb 12, 12:22 PM', smpikaEnd: 'Feb 13, 02:25 PM',
    vaishnavDate: '2026-02-13', vaishnavParanStart: '07:06 AM', vaishnavParanEnd: '09:07 AM', vaishnavParanNote: 'Feb 14',
  },
  { 
    date: '2026-02-27', name: 'Amalaki Ekadashi', paksha: 'Shukla', month: 'Phalguna', 
    description: 'Worship of Amalaki tree',
    smpikaStart: 'Feb 27, 12:33 AM', smpikaEnd: 'Feb 27, 10:32 PM',
    vaishnavDate: '2026-02-27', vaishnavParanStart: '06:51 AM', vaishnavParanEnd: '08:52 AM', vaishnavParanNote: 'Feb 28',
  },
  // 2026 (Mar-Dec) - Updated per Vaishnav Ekadashi attachment data
  { 
    date: '2026-03-15', name: 'Papamochani Ekadashi', paksha: 'Krishna', month: 'Chaitra', 
    description: 'Destroyer of sins',
    vaishnavDate: '2026-03-15', vaishnavParanStart: 'Sunrise', vaishnavParanEnd: '07:59 AM', vaishnavParanNote: 'Mar 16',
  },
  { 
    date: '2026-03-29', name: 'Kamada Ekadashi', paksha: 'Shukla', month: 'Chaitra', 
    description: 'Fulfiller of desires',
    vaishnavDate: '2026-03-29', vaishnavParanStart: 'Sunrise', vaishnavParanEnd: '07:38 AM', vaishnavParanNote: 'Mar 30',
  },
  { 
    date: '2026-04-13', name: 'Varuthini Ekadashi', paksha: 'Krishna', month: 'Vaishakha', 
    description: 'Protective fasting',
    vaishnavDate: '2026-04-13', vaishnavParanStart: 'Sunrise', vaishnavParanEnd: '09:33 AM', vaishnavParanNote: 'Apr 14',
  },
  { 
    date: '2026-04-27', name: 'Mohini Ekadashi', paksha: 'Shukla', month: 'Vaishakha', 
    description: 'Enchanting form of Vishnu',
    vaishnavDate: '2026-04-27', vaishnavParanStart: 'Sunrise', vaishnavParanEnd: '09:27 AM', vaishnavParanNote: 'Apr 28',
  },
  { 
    date: '2026-05-13', name: 'Apara Ekadashi', paksha: 'Krishna', month: 'Jyeshtha', 
    description: 'Unlimited merit',
    vaishnavDate: '2026-05-13', vaishnavParanStart: 'Sunrise', vaishnavParanEnd: '07:45 AM', vaishnavParanNote: 'May 14',
  },
  { 
    date: '2026-05-27', name: 'Padmini Ekadashi (Purushottam Maas)', paksha: 'Shukla', month: 'Adhik Jyeshtha', 
    description: 'Adhik Mas (extra month) Ekadashi',
    vaishnavDate: '2026-05-27', vaishnavParanStart: 'Sunrise', vaishnavParanEnd: '08:42 AM', vaishnavParanNote: 'May 28',
  },
  { 
    date: '2026-06-11', name: 'Parama Ekadashi (Purushottam Maas)', paksha: 'Krishna', month: 'Adhik Jyeshtha', 
    description: 'Adhik Mas (extra month) Ekadashi',
    vaishnavDate: '2026-06-11', vaishnavParanStart: 'Sunrise', vaishnavParanEnd: '09:23 AM', vaishnavParanNote: 'Jun 12',
  },
  { 
    date: '2026-06-25', name: 'Nirjala Ekadashi', paksha: 'Shukla', month: 'Jyeshtha', 
    description: 'Waterless fasting - most meritorious',
    vaishnavDate: '2026-06-25', vaishnavParanStart: 'Sunrise', vaishnavParanEnd: '09:26 AM', vaishnavParanNote: 'Jun 26',
  },
  { 
    date: '2026-07-11', name: 'Yogini Ekadashi', paksha: 'Krishna', month: 'Ashadha', 
    description: 'Union with divine',
    vaishnavDate: '2026-07-11', vaishnavParanStart: 'Sunrise', vaishnavParanEnd: '09:30 AM', vaishnavParanNote: 'Jul 12',
  },
  { 
    date: '2026-07-25', name: 'Dev Shayani Ekadashi', paksha: 'Shukla', month: 'Ashadha', 
    description: 'Vishnu goes to sleep - Chaturmas begins',
    vaishnavDate: '2026-07-25', vaishnavParanStart: 'Sunrise', vaishnavParanEnd: '09:32 AM', vaishnavParanNote: 'Jul 26',
  },
  { 
    date: '2026-08-09', name: 'Kamika Ekadashi', paksha: 'Krishna', month: 'Shravana', 
    description: 'During Chaturmas',
    vaishnavDate: '2026-08-09', vaishnavParanStart: 'Sunrise', vaishnavParanEnd: '06:17 AM', vaishnavParanNote: 'Aug 10',
  },
  { 
    date: '2026-08-24', name: 'Pavitra Ropan (Vyanjuli) Ekadashi', paksha: 'Shukla', month: 'Shravana', 
    description: 'Sacred thread ceremony Ekadashi',
    vaishnavDate: '2026-08-24', vaishnavParanStart: 'Sunrise', vaishnavParanEnd: '05:53 AM', vaishnavParanNote: 'Aug 25',
  },
  { 
    date: '2026-09-07', name: 'Annada Ekadashi', paksha: 'Krishna', month: 'Bhadrapada', 
    description: 'Giver of food/grains',
    vaishnavDate: '2026-09-07', vaishnavParanStart: 'Sunrise', vaishnavParanEnd: '09:32 AM', vaishnavParanNote: 'Sep 08',
  },
  { 
    date: '2026-09-23', name: 'Parshva (Vijaya) Ekadashi', paksha: 'Shukla', month: 'Bhadrapada', 
    description: 'Vishnu turns on his side',
    vaishnavDate: '2026-09-23', vaishnavParanStart: 'Sunrise', vaishnavParanEnd: '09:30 AM', vaishnavParanNote: 'Sep 24',
  },
  { 
    date: '2026-10-06', name: 'Indira Ekadashi', paksha: 'Krishna', month: 'Ashwin', 
    description: 'Liberation of ancestors',
    vaishnavDate: '2026-10-06', vaishnavParanStart: '06:30 AM', vaishnavParanEnd: '09:28 AM', vaishnavParanNote: 'Oct 07',
  },
  { 
    date: '2026-10-22', name: 'Pashankusha Ekadashi', paksha: 'Shukla', month: 'Ashwin', 
    description: 'Hook to destroy sins',
    vaishnavDate: '2026-10-22', vaishnavParanStart: 'Sunrise', vaishnavParanEnd: '09:28 AM', vaishnavParanNote: 'Oct 23',
  },
  { 
    date: '2026-11-05', name: 'Rama Ekadashi', paksha: 'Krishna', month: 'Kartik', 
    description: 'Dedicated to Lord Rama',
    vaishnavDate: '2026-11-05', vaishnavParanStart: 'Sunrise', vaishnavParanEnd: '09:30 AM', vaishnavParanNote: 'Nov 06',
  },
  { 
    date: '2026-11-20', name: 'Devotthana Ekadashi', paksha: 'Shukla', month: 'Kartik', 
    description: 'Vishnu awakens - Tulsi Vivah',
    vaishnavDate: '2026-11-20', vaishnavParanStart: '08:02 AM', vaishnavParanEnd: '09:35 AM', vaishnavParanNote: 'Nov 21',
  },
  { 
    date: '2026-12-04', name: 'Utpanna Ekadashi', paksha: 'Krishna', month: 'Margashirsha', 
    description: 'Birth of Ekadashi Devi',
    vaishnavDate: '2026-12-04', vaishnavParanStart: '07:44 AM', vaishnavParanEnd: '09:41 AM', vaishnavParanNote: 'Dec 05',
  },
  { 
    date: '2026-12-18', name: 'Mokshada Ekadashi', paksha: 'Shukla', month: 'Margashirsha', 
    description: 'Giver of liberation - Gita Jayanti',
    vaishnavDate: '2026-12-18', vaishnavParanStart: 'Sunrise', vaishnavParanEnd: '09:50 AM', vaishnavParanNote: 'Dec 20',
  },
  // 2027 (Jan-Apr) - Updated per Vaishnav Ekadashi attachment data
  { 
    date: '2027-01-03', name: 'Saphala Ekadashi', paksha: 'Krishna', month: 'Pausha', 
    description: 'Fruitful fasting',
    vaishnavDate: '2027-01-03', vaishnavParanStart: 'Sunrise', vaishnavParanEnd: '09:56 AM', vaishnavParanNote: 'Jan 04',
  },
  { 
    date: '2027-01-19', name: 'Putrada (Maha) Ekadashi', paksha: 'Shukla', month: 'Pausha', 
    description: 'For blessing of sons - Maha Ekadashi',
    vaishnavDate: '2027-01-19', vaishnavParanStart: 'Sunrise', vaishnavParanEnd: '10:01 AM', vaishnavParanNote: 'Jan 20',
  },
  { 
    date: '2027-02-02', name: 'Shattila Ekadashi', paksha: 'Krishna', month: 'Magha', 
    description: 'Observance with sesame seeds',
    vaishnavDate: '2027-02-02', vaishnavParanStart: 'Sunrise', vaishnavParanEnd: '10:02 AM', vaishnavParanNote: 'Feb 03',
  },
  { 
    date: '2027-02-17', name: 'Jaya (Bhim) Ekadashi', paksha: 'Shukla', month: 'Magha', 
    description: 'Victory over sins - Bhim Ekadashi',
    vaishnavDate: '2027-02-17', vaishnavParanStart: 'Sunrise', vaishnavParanEnd: '09:59 AM', vaishnavParanNote: 'Feb 18, Before 09:59 AM',
  },
  { 
    date: '2027-03-04', name: 'Vijaya (Unmilani) Ekadashi', paksha: 'Krishna', month: 'Phalguna', 
    description: 'Success in endeavors',
    vaishnavDate: '2027-03-04', vaishnavParanStart: 'Sunrise', vaishnavParanEnd: '09:53 AM', vaishnavParanNote: 'Mar 05',
  },
  { 
    date: '2027-03-18', name: 'Amalaki Ekadashi', paksha: 'Shukla', month: 'Phalguna', 
    description: 'Worship of Amalaki tree',
    vaishnavDate: '2027-03-18', vaishnavParanStart: 'Sunrise', vaishnavParanEnd: '09:46 AM', vaishnavParanNote: 'Mar 19',
  },
  { 
    date: '2027-04-02', name: 'Papamochani Ekadashi', paksha: 'Krishna', month: 'Chaitra', 
    description: 'Destroyer of sins',
    vaishnavDate: '2027-04-02', vaishnavParanStart: '08:11 AM', vaishnavParanEnd: '09:38 AM', vaishnavParanNote: 'Apr 03',
  },
];

export type ReminderType = 'day_before' | 'morning_of' | 'custom';

export interface EkadashiReminderSettings {
  enabled: boolean;
  reminderType: ReminderType;  // Type of reminder
  reminderDaysBefore: number;  // 1 = day before, 0 = same day (for custom)
  reminderTime: string;        // HH:MM format (for morning_of and custom)
  notificationsEnabled: boolean; // Browser notification permission
}